/**
 * Cartografía y autodetección GPS.
 *
 * Es la pantalla que decide si el control de GPS obligatorio de §9.4 puede
 * funcionar. Contiene cuatro cosas:
 *
 *  1. SEMÁFORO de cobertura: qué secciones del marco tienen geometría y cuáles
 *     no. Salir a campo con una sección sin polígono significa que la
 *     autodetección no valida nada ahí.
 *  2. GRUPOS DE EQUIVALENCIA entre el catálogo del marco (base 2024) y el de la
 *     cartografía vigente. El caso real es la sección 722: concentra el 16.1%
 *     del padrón y 6 conglomerados, pero no existe en el KMZ de mayo 2025
 *     porque el INE la reseccionó en las secciones 2216–2222.
 *  3. IMPORTACIÓN de KMZ convertido y del marco geoestadístico del INEGI.
 *  4. PROBADOR de coordenadas, para verificar la detección antes de campo.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GpsDetectionResult } from '@capi/shared';

import { api, ApiError } from '../api/client';
import { useAdmin } from '../store';
import { StatusChip, Tile, fmtInt } from '../components/charts';

export function CartographyPage() {
  const { can } = useAdmin();
  const queryClient = useQueryClient();

  const status = useQuery({ queryKey: ['cartography', 'status'], queryFn: api.cartographyStatus });
  const imports = useQuery({ queryKey: ['cartography', 'imports'], queryFn: api.cartographyImports });
  const equivalences = useQuery({ queryKey: ['frame', 'equivalences'], queryFn: api.equivalences });

  if (status.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  const data = status.data;
  if (!data) {
    return (
      <div className="callout callout--critical">No se pudo cargar el estado de la cartografía.</div>
    );
  }

  const missing = data.coverage.missingFromCartography;
  const unverified = data.coverage.unverifiedGroups;
  const resolved = data.coverage.resolvedByEquivalence;

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Cartografía y autodetección GPS</h1>
        <p className="page-sub">
          §9.4 exige validar que cada captura cae dentro del polígono de la sección INE. Para que eso
          funcione, las 46 secciones del marco necesitan geometría, directa o por equivalencia.
        </p>
      </div>

      {/* ---------------------------- Semáforo ------------------------------ */}

      {missing.length > 0 ? (
        <div className="callout callout--critical">
          <span className="callout__title">
            <StatusChip status="critical">
              {missing.length} sección(es) del marco sin geometría
            </StatusChip>
          </span>
          Secciones {missing.join(', ')}. La autodetección GPS no validará nada en esas áreas:
          importa la cartografía faltante o declara su grupo de equivalencia antes de capacitar.
        </div>
      ) : (
        <div className="callout callout--good">
          <span className="callout__title">
            <StatusChip status="good">Todas las secciones del marco tienen geometría</StatusChip>
          </span>
          {fmtInt(data.loadedSectionCount)} geometrías cargadas cubren las{' '}
          {fmtInt(data.frameSectionCount)} secciones del marco.
          {resolved.length > 0
            ? ` ${resolved.length} se resuelve(n) por grupo de equivalencia: ${resolved.join(', ')}.`
            : ''}
        </div>
      )}

      {unverified.length > 0 ? (
        <div className="callout callout--warning">
          <span className="callout__title">
            <StatusChip status="warning">
              Equivalencia pendiente de confirmar: sección {unverified.join(', ')}
            </StatusChip>
          </span>
          La correspondencia se infirió por geografía, no es un dato oficial. Confírmala contra el
          catálogo de secciones del INE o el encarte IEEBC vigente y márcala como verificada.
        </div>
      ) : null}

      {!data.postgisAvailable ? (
        <div className="callout callout--warning">
          <span className="callout__title">
            <StatusChip status="warning">PostGIS no disponible</StatusChip>
          </span>
          La autodetección está usando el cálculo en Node sobre geometrías en memoria. Funciona a
          nivel de sección, pero no sería viable con las decenas de miles de manzanas del marco
          geoestadístico. Habilita la extensión en el clúster con{' '}
          <span className="mono">CREATE EXTENSION postgis;</span>
        </div>
      ) : null}

      {/* ------------------------------ Capas ------------------------------- */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Capas disponibles</h2>
            <p className="panel__note">
              La autodetección usa la capa más fina que esté cargada. Nivel efectivo actual:{' '}
              <span className="strong">{levelLabel(data.finestLevel)}</span>.
            </p>
          </div>
        </div>

        <div className="grid grid--4">
          {data.layers.map((layer) => (
            <Tile
              key={layer.level}
              label={layer.label}
              value={layer.loaded ? fmtInt(layer.featureCount) : '—'}
              meta={
                layer.loaded ? (
                  layer.source
                ) : (
                  <>
                    Sin cargar
                    {layer.fallbackLevel
                      ? ` · la validación baja a ${levelLabel(layer.fallbackLevel)}`
                      : ''}
                  </>
                )
              }
            />
          ))}
        </div>

        <details className="note">
          <summary>Por qué importan los niveles finos</summary>
          <p>
            Con sólo la capa de secciones, una captura valida si cae en cualquier punto de la
            sección, que en el rural puede abarcar cientos de kilómetros cuadrados: la sección 733
            (La Rumorosa) mide cerca de 392 km². Con las manzanas cargadas, la validación
            baja a la manzana y el sorteo de manzanas de arranque de §5.3.1 se vuelve posible.
          </p>
        </details>
      </div>

      {/* ------------------------- Equivalencias ---------------------------- */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Grupos de equivalencia de secciones</h2>
            <p className="panel__note">
              Traducen el catálogo del marco (construido con el cómputo por casilla de 2024) al
              catálogo de la cartografía vigente. Un GPS dentro de cualquiera de las secciones
              equivalentes valida como la sección del marco, de modo que los conglomerados y los
              pesos de §4.2 y §6 se conservan sin cambios.
            </p>
          </div>
        </div>

        {equivalences.data && equivalences.data.length > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Sección del marco</th>
                  <th>Secciones de la cartografía vigente</th>
                  <th>Motivo</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {equivalences.data.map((group) => (
                  <EquivalenceRow
                    key={group.frameSeccion}
                    group={group}
                    canEdit={can('frame:write')}
                    onSaved={() => {
                      void queryClient.invalidateQueries({ queryKey: ['frame', 'equivalences'] });
                      void queryClient.invalidateQueries({ queryKey: ['cartography', 'status'] });
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">No hay grupos de equivalencia declarados.</p>
        )}
      </div>

      {/* --------------------------- Probador GPS --------------------------- */}

      <GpsTester />

      {/* --------------------------- Importación ---------------------------- */}

      {can('cartography:write') ? <ImportPanel /> : null}

      {/* ---------------------------- Historial ----------------------------- */}

      {imports.data && imports.data.length > 0 ? (
        <div className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Historial de importaciones</h2>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Tipo</th>
                  <th>Geometrías</th>
                  <th>Fecha</th>
                  <th>Importó</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {imports.data.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="strong">{item.filename}</div>
                      {item.sourceName ? <div className="faint">{item.sourceName}</div> : null}
                    </td>
                    <td className="faint">{kindLabel(item.kind)}</td>
                    <td className="num">{fmtInt(item.featureCount)}</td>
                    <td className="faint">
                      {new Date(item.createdAt).toLocaleString('es-MX', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="faint">{item.importedBy?.name ?? '—'}</td>
                    <td>
                      {item.isActive ? (
                        <StatusChip status="good">Activa</StatusChip>
                      ) : (
                        <StatusChip status="neutral">Histórica</StatusChip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                         Fila de equivalencia                               */
/* -------------------------------------------------------------------------- */

function EquivalenceRow({
  group,
  canEdit,
  onSaved,
}: {
  group: {
    frameSeccion: number;
    currentSecciones: number[];
    reason: string;
    verified: boolean;
    note: string | null;
  };
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [secciones, setSecciones] = useState(group.currentSecciones.join(', '));
  const [verified, setVerified] = useState(group.verified);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const parsed = secciones
        .split(/[,\s]+/)
        .map((s) => Number.parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n));
      return api.saveEquivalence({
        frameSeccion: group.frameSeccion,
        currentSecciones: parsed,
        reason: group.reason,
        verified,
        ...(group.note ? { note: group.note } : {}),
      });
    },
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onSaved();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo guardar.'),
  });

  return (
    <tr>
      <td>
        <span className="strong num">{group.frameSeccion}</span>
      </td>
      <td>
        {editing ? (
          <input
            className="input"
            value={secciones}
            onChange={(e) => setSecciones(e.target.value)}
            style={{ minWidth: 240 }}
          />
        ) : (
          <span className="mono">{group.currentSecciones.join(', ')}</span>
        )}
        {error ? <div className="field__error">{error}</div> : null}
      </td>
      <td className="faint">{reasonLabel(group.reason)}</td>
      <td>
        {editing ? (
          <label className="row" style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
            />
            Confirmada con el INE
          </label>
        ) : group.verified ? (
          <StatusChip status="good">Verificada</StatusChip>
        ) : (
          <StatusChip status="warning">Inferida</StatusChip>
        )}
        {group.note && !editing ? (
          <details className="note">
            <summary>Nota</summary>
            <p>{group.note}</p>
          </details>
        ) : null}
      </td>
      <td>
        {canEdit ? (
          editing ? (
            <div className="row">
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => save.mutate()}
                disabled={save.isPending}
              >
                Guardar
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setEditing(false);
                  setSecciones(group.currentSecciones.join(', '));
                  setVerified(group.verified);
                  setError(null);
                }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditing(true)}>
              Editar
            </button>
          )
        ) : null}
      </td>
    </tr>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Probador de GPS                                 */
/* -------------------------------------------------------------------------- */

/**
 * Probador de coordenadas.
 *
 * Permite verificar la autodetección antes de capacitar: se pega una
 * coordenada, se indica la sección esperada y se ve exactamente lo que verá el
 * encuestador en campo, incluida la resolución por equivalencia y la distancia
 * en metros al polígono.
 */
function GpsTester() {
  const [latitude, setLatitude] = useState('32.551215');
  const [longitude, setLongitude] = useState('-116.642201');
  const [accuracy, setAccuracy] = useState('12');
  const [expected, setExpected] = useState('722');
  const [result, setResult] = useState<GpsDetectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const detect = useMutation({
    mutationFn: () =>
      api.detectGps({
        latitude: Number.parseFloat(latitude),
        longitude: Number.parseFloat(longitude),
        accuracyMeters: accuracy ? Number.parseFloat(accuracy) : null,
        expectedFrameSeccion: expected ? Number.parseInt(expected, 10) : null,
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (e) => {
      setResult(null);
      setError(e instanceof ApiError ? e.message : 'No se pudo consultar.');
    },
  });

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">Probador de autodetección</h2>
          <p className="panel__note">
            Verifica una coordenada contra la cartografía cargada y muestra exactamente lo que verá
            el encuestador, incluida la traducción por equivalencia y la distancia al polígono.
          </p>
        </div>
      </div>

      <div className="filters" style={{ marginBottom: 14 }}>
        <div className="field">
          <label className="field__label" htmlFor="lat">
            Latitud
          </label>
          <input id="lat" className="input" value={latitude} onChange={(e) => setLatitude(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="lon">
            Longitud
          </label>
          <input id="lon" className="input" value={longitude} onChange={(e) => setLongitude(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="acc">
            Precisión (m)
          </label>
          <input id="acc" className="input" value={accuracy} onChange={(e) => setAccuracy(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="exp">
            Sección esperada
          </label>
          <input id="exp" className="input" value={expected} onChange={(e) => setExpected(e.target.value)} />
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => detect.mutate()}
          disabled={detect.isPending}
        >
          {detect.isPending ? 'Consultando…' : 'Detectar'}
        </button>
      </div>

      {error ? <div className="callout callout--critical">{error}</div> : null}

      {result ? (
        <div
          className={`callout callout--${
            result.valid ? 'good' : result.requiresReason ? 'warning' : 'info'
          }`}
          style={{ marginBottom: 0 }}
        >
          <span className="callout__title">
            <StatusChip status={result.valid ? 'good' : 'warning'}>{result.status}</StatusChip>
          </span>
          <p style={{ margin: '6px 0' }}>{result.message}</p>
          <div className="grid grid--3" style={{ marginTop: 12 }}>
            <div>
              <div className="faint">Detectada en cartografía</div>
              <div className="strong num">{result.detectedSeccion ?? '—'}</div>
            </div>
            <div>
              <div className="faint">Sección del marco</div>
              <div className="strong num">{result.detectedFrameSeccion ?? '—'}</div>
            </div>
            <div>
              <div className="faint">Distancia al polígono esperado</div>
              <div className="strong num">
                {result.distanceToExpectedMeters === null
                  ? '—'
                  : `${Math.round(result.distanceToExpectedMeters)} m`}
              </div>
            </div>
            <div>
              <div className="faint">Conglomerado detectado</div>
              <div className="strong">{result.detectedClusterId ?? '—'}</div>
            </div>
            <div>
              <div className="faint">Manzana detectada</div>
              <div className="strong mono">{result.detectedManzanaId ?? '—'}</div>
            </div>
            <div>
              <div className="faint">Exige motivo del encuestador</div>
              <div className="strong">{result.requiresReason ? 'Sí' : 'No'}</div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Importación                                   */
/* -------------------------------------------------------------------------- */

function ImportPanel() {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'sections' | 'manzanas'>('sections');
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new ApiError(400, 'Selecciona un archivo GeoJSON.');
      const text = await file.text();
      const collection = JSON.parse(text) as unknown;
      const body = { collection, filename: file.name, activate: true };
      return kind === 'sections' ? api.importSections(body) : api.importManzanas(body);
    },
    onSuccess: (result) => {
      const lines: string[] = [];
      if ('blockingIssues' in result && result.blockingIssues.length > 0) {
        lines.push(...result.blockingIssues);
      }
      if ('assignedToSection' in result) {
        lines.push(
          `${fmtInt(result.featureCount)} manzanas importadas · ${fmtInt(result.assignedToSection)} asignadas a una sección · ${fmtInt(result.unassigned)} sin sección.`,
        );
      } else {
        lines.push(`${fmtInt(result.featureCount)} secciones importadas.`);
      }
      lines.push(...result.warnings);
      setReport(lines);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['cartography'] });
      void queryClient.invalidateQueries({ queryKey: ['frame'] });
    },
    onError: (e) => {
      setReport(null);
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? `No se pudo leer el archivo: ${e.message}`
            : 'Error desconocido.',
      );
    },
  });

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">Importar cartografía</h2>
          <p className="panel__note">
            Acepta GeoJSON. Para el KMZ del INE, conviértelo primero con{' '}
            <span className="mono">npm run kmz --workspace @capi/cartography -- --municipio 3</span>,
            que además audita la cobertura contra las 46 secciones del marco.
          </p>
        </div>
      </div>

      <div className="filters">
        <div className="field">
          <label className="field__label" htmlFor="kind">
            Tipo
          </label>
          <select
            id="kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'sections' | 'manzanas')}
          >
            <option value="sections">Secciones electorales (INE)</option>
            <option value="manzanas">Manzanas (BGDE del INE o marco del INEGI)</option>
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label" htmlFor="file">
            Archivo GeoJSON
          </label>
          <input
            id="file"
            className="input"
            type="file"
            accept=".geojson,.json,application/geo+json,application/json"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => importMutation.mutate()}
          disabled={!file || importMutation.isPending}
        >
          {importMutation.isPending ? 'Importando…' : 'Importar'}
        </button>
      </div>

      {kind === 'manzanas' ? (
        <div className="callout callout--info">
          <span className="callout__title">Cómo se asigna la sección a cada manzana</span>
          Por intersección geométrica con los polígonos de sección ya cargados, tomando la sección
          con la que la manzana tiene mayor superficie de intersección. El marco geoestadístico del
          INEGI es una división censal y no incluye la sección electoral, porque son dos geografías
          distintas. Requiere haber importado antes las secciones.
        </div>
      ) : null}

      {error ? <div className="callout callout--critical">{error}</div> : null}

      {report ? (
        <div className="callout callout--good">
          <span className="callout__title">Importación completada</span>
          {report.map((line, i) => (
            <p key={i} style={{ margin: '4px 0' }}>
              {line}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Auxiliares                                  */
/* -------------------------------------------------------------------------- */

function levelLabel(level: string): string {
  const labels: Record<string, string> = {
    manzana: 'manzana',
    conglomerado: 'conglomerado',
    'area-extraordinaria': 'área de casilla extraordinaria',
    seccion: 'sección',
  };
  return labels[level] ?? level;
}

function kindLabel(kind: string): string {
  const labels: Record<string, string> = {
    // El tipo nombra la CAPA, no su procedencia. Atribuir aquí la fuente
    // hacía que una importación del BGDE apareciera en el historial como
    // «KMZ del INE», porque el valor del enum es histórico y no cambia con el
    // archivo. De dónde vino cada lote lo dice `sourceName`, que sí es por
    // importación y se muestra debajo del nombre del archivo.
    secciones_kmz: 'Secciones electorales',
    manzanas_inegi: 'Manzanas',
    secciones_ine: 'Secciones electorales',
    manzanas_ine: 'Manzanas',
    colonias_ine: 'Colonias',
    geojson_generico: 'GeoJSON genérico',
  };
  return labels[kind] ?? kind;
}

function reasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    resectioning: 'Reseccionamiento',
    merge: 'Fusión',
    renumber: 'Renumeración',
  };
  return labels[reason] ?? reason;
}
